// Typed client for the Product Control Plane BFF.
//
// Every call goes through `apiCall`, so it inherits the session bearer, the
// `credentials: "omit"` policy, the 401 handling and the RouteNotFoundError
// behaviour the rest of this SPA already relies on. Nothing here reaches D1,
// signs anything, or holds a credential.

import { apiCall, type ApiCallOptions } from "../api/client";
import type { ControlPlaneDocument, Lane } from "./contract";

const BASE = "/v2/admin/bff/control-plane";

/**
 * Every call in this family opts out of the default 404 handling.
 *
 * `apiCall` turns a 404 into `RouteNotFoundError` so a lost BE route is loud.
 * On this family a 404 is usually an ANSWER — unknown product, unknown
 * revision, product not available, no published revision — and reporting
 * "the BE route registration may have regressed" for "you asked for a
 * revision that does not exist" sends an operator to look at the wrong thing.
 * `classifyControlPlaneError` still reports a bodyless 404 as `route_missing`,
 * so nothing about the regression signal is given up.
 */
function cp<T>(path: string, opts: ApiCallOptions = {}): Promise<T> {
  return apiCall<T>(path, { ...opts, resourceNotFound: true });
}

const pid = (productId: string): string => encodeURIComponent(productId);

/** How the registry classifies an entry. The registry is the product
 *  EVIDENCE the genesis catalog is derived from (gao-id-worker migration
 *  0082): `kind` and `status` decide, per row, whether it is a current
 *  standalone product; `catalog` is the server's verdict from that rule —
 *  the same function the bootstrap preview acts on. */
export type ProductKind = "product" | "capability" | "infrastructure";
export type ProductStatus = "current" | "future" | "retired";

export interface ProductRow {
  productId: string;
  displayName: string;
  enabled: boolean;
  kind: ProductKind;
  status: ProductStatus;
  /** For a capability: the product it is represented through. */
  parentProductId: string | null;
  url: string | null;
  sortOrder: number;
  /** Whether a genesis catalog would carry this entry, and if not, why. */
  catalog: { eligible: true; reason: null } | { eligible: false; reason: string };
  /** Whether it owns a publishable document, and of which component types —
   *  separate from being an Explorer entry (gao-local-ai owns one without being
   *  a tile). Absent from servers that predate it. */
  document?: DocumentVerdict;
  currentRevisionId: string | null;
  pointerSeq: number | null;
  publishedAt: string | null;
  publishedBy: string | null;
  documentHash: string | null;
  hasUnpublishedDraft: boolean;
}

/** The server's verdict on whether a product owns a publishable Control Plane document. */
export interface DocumentVerdict {
  publishable: boolean;
  componentTypes: string[];
}

/** The governed-write affordances (bootstrap, edit, publish, rollback) follow the SERVER's document verdict; a server
 *  that predates it answered the same question with the catalog verdict. */
export function ownsPublishableDocument(p: { catalog?: { eligible: boolean }; document?: DocumentVerdict }): boolean {
  return p.document ? p.document.publishable : p.catalog?.eligible === true;
}

export interface RegistryResponse {
  ok: true;
  lane: Lane;
  issuer: string;
  laneRecognized: boolean;
  products: ProductRow[];
  requestId: string;
}

export interface PublishedView {
  revisionId: string;
  revisionSeq: number;
  schemaVersion: number;
  documentHash: string;
  publishedAt: string;
  publishedBy: string;
  publishedByLabel: string | null;
  document: ControlPlaneDocument;
}

export interface DraftMeta {
  documentHash: string;
  baseRevisionId: string | null;
  updatedAt: string;
  updatedBy: string;
  derivedFromPublished: boolean;
  matchesPublished: boolean;
}

export interface ProductDetailResponse {
  ok: true;
  lane: Lane;
  issuer: string;
  laneRecognized: boolean;
  product: {
    productId: string;
    displayName: string;
    enabled: boolean;
    kind: ProductKind;
    status: ProductStatus;
    /** For a capability: the product it is represented through. */
    parentProductId: string | null;
    url: string | null;
    sortOrder: number;
    /** Whether a genesis catalog would carry this entry, and if not, why —
     *  the same verdict the list route reports. The console draws the
     *  governed-write affordances only for a current standalone product; a
     *  capability, an infrastructure surface or a future/retired entry has no
     *  publishable document in this model. */
    catalog: { eligible: true; reason: null } | { eligible: false; reason: string };
    document?: DocumentVerdict;
    createdAt: string;
  };
  current: {
    revisionId: string;
    pointerSeq: number;
    updatedAt: string;
    updatedBy: string;
  } | null;
  published: PublishedView | null;
  publishedError: string | null;
  draft: DraftMeta | null;
  requestId: string;
}

export interface DraftResponse extends DraftMeta {
  ok: true;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface DocumentIssue {
  path: string;
  code: string;
  message: string;
  itemScoped: boolean;
}

export interface ValidationReport {
  ok: true;
  valid: boolean;
  issues: DocumentIssue[];
  documentHash: string | null;
  byteLength: number | null;
  requestId: string;
}

export interface RevisionSummary {
  revisionId: string;
  revisionSeq: number;
  schemaVersion: number;
  documentHash: string;
  publishedAt: string;
  publishedBy: string;
  publishedByLabel: string | null;
  isCurrent: boolean;
}

export interface RevisionsResponse {
  ok: true;
  currentRevisionId: string | null;
  limit: number;
  revisions: RevisionSummary[];
  requestId: string;
}

export interface RevisionResponse extends Omit<RevisionSummary, "isCurrent"> {
  ok: true;
  isCurrent: boolean;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface AuditEvent {
  id: number;
  occurredAt: string;
  actor: string;
  actorLabel: string | null;
  action:
    | "DRAFT_CREATED"
    | "DRAFT_UPDATED"
    | "DRAFT_RESET"
    | "VALIDATED"
    | "PUBLISHED"
    | "ROLLED_BACK";
  productId: string;
  surfaceId: string | null;
  fromRevision: string | null;
  toRevision: string | null;
  documentHash: string | null;
  result: "ok" | "rejected" | "error";
  detail: string | null;
  requestId: string | null;
}

export interface AuditResponse {
  ok: true;
  limit: number;
  events: AuditEvent[];
  requestId: string;
}

export interface PreviewResponse {
  ok: true;
  source: "published" | "draft";
  lane: Lane;
  appVersion: string | null;
  appVersionDeclared: string | null;
  revisionId?: string;
  document: ControlPlaneDocument;
  requestId: string;
}

export interface PublishResponse {
  ok: true;
  revisionId: string;
  revisionSeq: number;
  documentHash: string;
  publishedAt: string;
  previousRevisionId: string | null;
  requestId: string;
}

export interface RollbackResponse {
  ok: true;
  fromRevisionId: string;
  toRevisionId: string;
  documentHash: string;
  requestId: string;
}

export interface BootstrapResponse {
  ok: true;
  revisionId: string;
  documentHash: string;
  publishedAt: string;
  migrated: {
    /** The table the entries were derived from — stated by the server. */
    source: string;
    sourceRowCount: number;
    carriedCount: number;
    dropped: Array<{ id: string; reason: string }>;
  };
  requestId: string;
}

// ── Governed operations ─────────────────────────────────────────────────────

/** The three Control Plane acts that require an approved GaoSafe proposal.
 *
 *  `draft.save` is deliberately absent: the server classifies it
 *  `single_admin` — a draft is invisible to every consumer and the save
 *  carries its own compare-and-set — so demanding a threshold there would be
 *  ceremony with no boundary behind it. */
export type ControlPlaneOperation = "bootstrap" | "publish" | "rollback";

export const CONTROL_PLANE_OPERATIONS: ReadonlyArray<ControlPlaneOperation> = [
  "bootstrap",
  "publish",
  "rollback",
];

/** The classes a Control Plane proposal can be minted under. The server
 *  refuses to mint one for `read` or `single_admin`, so those cannot appear. */
export type ProposalActionClass =
  | "low_config"
  | "high_config"
  | "money_movement"
  | "production_deploy";

/** What the SERVER decided this proposal is for. Every field is the server's
 *  answer — the console sends an operation and, for a rollback, a revision id,
 *  and reads back the lane, the class, the threshold, the expiry and the exact
 *  target the approval will commit to. */
export interface ProposalResponse {
  ok: true;
  proposalId: string;
  actionType: ProposalActionClass;
  thresholdRequired: number;
  expiresAt: string;
  lane: Lane;
  operation: ControlPlaneOperation;
  productId: string;
  /** publish → the candidate document hash; rollback → the revision id;
   *  bootstrap → null, because genesis has no prior target. */
  target: string | null;
  requestId: string;
}

// ── Reads ───────────────────────────────────────────────────────────────────

export function listProducts(): Promise<RegistryResponse> {
  return cp<RegistryResponse>(`${BASE}/products`);
}

export function getProduct(productId: string): Promise<ProductDetailResponse> {
  return cp<ProductDetailResponse>(`${BASE}/products/${pid(productId)}`);
}

export function getDraft(productId: string): Promise<DraftResponse> {
  return cp<DraftResponse>(`${BASE}/products/${pid(productId)}/draft`);
}

export function listRevisions(productId: string): Promise<RevisionsResponse> {
  return cp<RevisionsResponse>(`${BASE}/products/${pid(productId)}/revisions`);
}

export function getRevision(
  productId: string,
  revisionId: string,
): Promise<RevisionResponse> {
  return cp<RevisionResponse>(
    `${BASE}/products/${pid(productId)}/revisions/${encodeURIComponent(revisionId)}`,
  );
}

export function listAudit(productId: string): Promise<AuditResponse> {
  return cp<AuditResponse>(`${BASE}/products/${pid(productId)}/audit`);
}

/** What a consumer on `lane` declaring `appVersion` would actually receive.
 *  The lane may be chosen here — and ONLY here — because this is a preview
 *  behind the owner gate, never a consumer response. */
export function preview(
  productId: string,
  opts: { lane?: Lane; appVersion?: string; source?: "published" | "draft" } = {},
): Promise<PreviewResponse> {
  return cp<PreviewResponse>(`${BASE}/products/${pid(productId)}/preview`, {
    query: {
      lane: opts.lane,
      appVersion: opts.appVersion,
      source: opts.source,
    },
  });
}

/** What a bootstrap would publish, and the digest its approval commits to.
 *
 *  A READ. It derives the genesis document from the Product Registry — every
 *  registered entry, carried or not, with the reason for each one that is not
 *  — and hashes it, writing nothing, so the review exists before there is
 *  anything to approve, and stays reachable while the write gate is closed,
 *  which is the only window in which an operator can prepare. */
export interface BootstrapPreviewResponse {
  ok: true;
  productId: string;
  lane: Lane;
  /** The exact value a bootstrap proposal will carry, in the form the executor
   *  compares. `genesis-content-sha256:<hex>`. Displayed, never sent. */
  target: string;
  contentDigest: string;
  /** Set when a revision already exists, in which case bootstrap is refused. */
  alreadyPublished: string | null;
  migrated: {
    /** The table the entries were derived from — stated by the server, so
     *  this console cannot present a registry derivation as anything else. */
    source: string;
    sourceRowCount: number;
    carriedCount: number;
    dropped: Array<{ id: string; reason: string }>;
  };
  document: ControlPlaneDocument;
  requestId: string;
}

export function bootstrapPreview(productId: string): Promise<BootstrapPreviewResponse> {
  return cp<BootstrapPreviewResponse>(
    `${BASE}/products/${pid(productId)}/bootstrap/preview`,
  );
}

// ── Asking for authority ────────────────────────────────────────────────────

/**
 * Ask the server to mint a proposal for a privileged act.
 *
 * The console sends the operation — and, for a rollback, which revision — and
 * NOTHING ELSE that matters. The server derives the lane from its own
 * `AUTH_V2_ISS`, derives the target from stored state (the draft's hash for a
 * publish, the named revision for a rollback), picks the action name from
 * (operation, lane), classifies it, and mints the payload digest the executor
 * will later re-derive and compare.
 *
 * That division is the whole security property, so this function must never
 * grow a `lane`, `actionType`, `threshold` or `target` parameter. A client that
 * could name its own lane could mint a cheap dev-class approval and spend it on
 * Product; a client that could name its own target could get one document
 * approved and publish another.
 *
 * The returned proposal is in `proposed` state. Approving it is a separate act
 * by a signer on the Approvals screen.
 */
export function createProposal(
  productId: string,
  input: { operation: ControlPlaneOperation; revisionId?: string },
): Promise<ProposalResponse> {
  const body: Record<string, unknown> = { operation: input.operation };
  if (input.operation === "rollback") {
    body.revisionId = input.revisionId;
  }
  return cp<ProposalResponse>(`${BASE}/products/${pid(productId)}/proposals`, {
    method: "POST",
    body,
  });
}

// ── Writes ──────────────────────────────────────────────────────────────────

/**
 * Store the draft, declaring which draft this edit was made against.
 *
 * `expectedDocumentHash` is REQUIRED by the server, and `null` is a real value
 * rather than an omission: it means "I expect this product to have no draft and
 * no published revision". Sending nothing at all is a 400, because a client
 * that may omit the token keeps last-write-wins — which is the behaviour the
 * server-side check exists to remove.
 *
 * A 409 means someone else saved between this operator's load and their save.
 * It surfaces as an `ApiHttpError`; the caller reloads rather than retrying,
 * because retrying with the same stale hash would only be refused again.
 *
 * No proposalId: the server classifies a draft save `single_admin`.
 */
export function saveDraft(
  productId: string,
  document: ControlPlaneDocument,
  expectedDocumentHash: string | null,
): Promise<{ ok: true; documentHash: string; issues: DocumentIssue[]; requestId: string }> {
  return cp(`${BASE}/products/${pid(productId)}/draft`, {
    method: "PUT",
    body: { document, expectedDocumentHash },
  });
}

export function resetDraft(
  productId: string,
): Promise<{ ok: true; documentHash: string; resetToRevisionId: string; requestId: string }> {
  return cp(`${BASE}/products/${pid(productId)}/draft/reset`, { method: "POST" });
}

export function validateDraft(
  productId: string,
  document?: ControlPlaneDocument,
): Promise<ValidationReport> {
  return cp<ValidationReport>(`${BASE}/products/${pid(productId)}/validate`, {
    method: "POST",
    body: document ? { document } : undefined,
  });
}

/** Mint revision 1 from the Product Registry's current products. Idempotent
 *  by refusal — 409 once any revision exists.
 *
 *  Requires an approved proposal whose payload commits to (bootstrap, this
 *  product, this lane, AND the content digest of the genesis document). The
 *  server re-derives that digest from the registry at execution and refuses
 *  if it moved, so an approval collected against one registry state cannot
 *  execute against another. */
export function bootstrap(
  productId: string,
  proposalId: string,
): Promise<BootstrapResponse> {
  return cp<BootstrapResponse>(`${BASE}/products/${pid(productId)}/bootstrap`, {
    method: "POST",
    body: { proposalId },
  });
}

/**
 * Publish the draft.
 *
 * Two independent guards travel with this call and neither replaces the other.
 *
 * `proposalId` is AUTHORITY: an approved proposal whose payload commits to
 * (publish, this product, this lane, this draft's document hash). The server
 * re-derives all four and refuses a proposal that authorises anything else.
 *
 * `expectedBaseRevisionId` is CONCURRENCY: the revision this console believes
 * is live. If someone else published in between, the publish is refused rather
 * than burying their revision under this operator's older draft.
 *
 * A proposal cannot stand in for the CAS token — it commits to the draft that
 * is being published, not to what was live when it was minted — and the CAS
 * token cannot stand in for the proposal.
 */
export function publish(
  productId: string,
  expectedBaseRevisionId: string | null,
  proposalId: string,
): Promise<PublishResponse> {
  const body: Record<string, unknown> = { proposalId };
  if (expectedBaseRevisionId !== null) {
    body.expectedBaseRevisionId = expectedBaseRevisionId;
  }
  return cp<PublishResponse>(`${BASE}/products/${pid(productId)}/publish`, {
    method: "POST",
    body,
  });
}

/** Move the live pointer back to an existing immutable revision. Requires an
 *  approved proposal whose target IS `revisionId` — an approval to roll back to
 *  one revision cannot be spent rolling back to another. */
export function rollback(
  productId: string,
  revisionId: string,
  proposalId: string,
): Promise<RollbackResponse> {
  return cp<RollbackResponse>(`${BASE}/products/${pid(productId)}/rollback`, {
    method: "POST",
    body: { revisionId, proposalId },
  });
}
